import { Button, Descriptions, Drawer, Link, Space, Table } from "@arco-design/web-react";
import { POAPI } from "@/apis/purchasing";
import { ItemAPI, InventoryAPI } from "@/apis/material";
import { useState } from "react";
import { Number, Currency } from "@/components/Numeric";
import { ItemSafetyStockType, ItemTypeDef } from "@/enums/material";
import { EnumLabel } from "@/components/Enum";

export const ItemInfoLink = ({ value, children }) => {
    const [visible, setVisible] = useState(false);
    const [itemId] = useState(value);
    const [itemInfo, setItemInfo] = useState({});
    const [inventoryStock, setInventoryStock] = useState([]);
    const [poListTotal, setPOListTotal] = useState(0);
    const [poList, setPOList] = useState([]);
    const [poPage, setPOPage] = useState(1);
    const [cogsAccountLabel, setCogsAccountLabel] = useState("COGS Account");

    const getInfo = async () => {
        const info = await ItemAPI.get(itemId);
        setItemInfo(info);
        setCogsAccountLabel(info.itemType === 2 ? "COGS/Expense Account" : "COGS Account");
    }

    const getInventoryStock = async () => {
        const stock = await InventoryAPI.stock(itemId);
        setInventoryStock(stock);
    }

    const getPOs = async (page) => {
        const poInfo = await POAPI.queryByItemId(page ?? 1, itemId);
        setPOList(poInfo.records);
        setPOListTotal(poInfo.total);
    }

    const getSafetyStockLevel = (item) => {
        switch (item.safetyStockType) {
            case ItemSafetyStockType.Days:
                return (
                    <Space>
                        <span>{item.safetyStockLevel} days</span>
                        {item.safetyStockQuantity != null &&
                            <span><Number value={item.safetyStockQuantity} /> {item.uom} updated on {item.safetyQuantityUpdateTime}</span>
                        }
                    </Space>
                )
            case ItemSafetyStockType.UOM:
                return item.safetyStockLevelUom + " " + item.uom;
            default:
                return "";
        }
    }

    return (
        <>
            <Link onClick={() => {
                getInfo();
                getInventoryStock();
                getPOs();
                setVisible(true);
            }}>{children}</Link>
            <Drawer visible={visible} width={800}
                title={children}
                footer={
                    <Button type="primary" onClick={() => setVisible(false)}>Close</Button>
                }
                onCancel={() => setVisible(false)}>
                <Descriptions column={2} colon=':'
                    labelStyle={{ paddingRight: 36 }}
                    data={[{
                        span: 2,
                        label: "Item Type",
                        value: <EnumLabel enumObj={ItemTypeDef} value={itemInfo.itemType} />
                    }, {
                        span: 2,
                        label: "Item Number",
                        value: itemInfo.itemNo
                    }, {
                        span: 2,
                        label: "Item Name",
                        value: itemInfo.itemName
                    }, {
                        span: 2,
                        label: "Description",
                        value: itemInfo.description
                    }, {
                        span: 2,
                        label: "Standard Cost",
                        value: <Currency value={itemInfo.stdCost} />
                    }, {
                        span: 2,
                        label: "Unit of Measure(UOM)",
                        value: itemInfo.uom
                    }, {
                        span: 2,
                        label: "Additional UOM",
                        value: itemInfo.additionalUom ? <span>{itemInfo.additionalUom} = <Number value={itemInfo.additionalUomThreshold} /> {itemInfo.uom}</span> : ""
                    }, {
                        span: 2,
                        label: "Default Margin",
                        value: itemInfo.defaultMargin
                    }, {
                        span: 2,
                        label: "Sales Price",
                        value: <Currency value={itemInfo.salePrice} />
                    }, {
                        span: 2,
                        label: cogsAccountLabel,
                        value: itemInfo.cogsAccount ? itemInfo.cogsAccount.accountName : ""
                    }, {
                        span: 2,
                        label: "Income Account",
                        value: itemInfo.incomeAccount ? itemInfo.incomeAccount.accountName : ""
                    }, {
                        span: 2,
                        label: "Inventory Account",
                        value: itemInfo.inventoryAccount ? itemInfo.inventoryAccount.accountName : ""
                    }, {
                        span: 2,
                        label: "Safety Stock Level",
                        value: getSafetyStockLevel(itemInfo)
                    }, {
                        label: "Preferred Vendor",
                        value: itemInfo.preferredVendor ? itemInfo.preferredVendor.vendorName : ""
                    }, {
                        label: "Lead Time",
                        value: itemInfo.preferredVendorLeadTime
                    }, {
                        label: "Vendor",
                        value: itemInfo.vendor ? itemInfo.vendor.vendorName : ""
                    }, {
                        label: "Lead Time",
                        value: itemInfo.vendorLeadTime
                    }]} />

                <Table rowKey="id" columns={[
                    { title: "Warehouse", dataIndex: "warehouseName" },
                    { title: "Location", dataIndex: "locationName" },
                    { title: "Lot#", dataIndex: "lot" },
                    { title: "Expiration Date", dataIndex: "expiredDate" },
                    { title: "Inventory OnHand", render: (col, record) => <Number value={record.onHand} /> },
                    { title: "Inventory Status", dataIndex: "status" },
                ]}
                    data={inventoryStock.filter(r => r.onHand !== 0)}
                />

                <Table rowKey="id" style={{ marginTop: 10 }} columns={[
                    { title: "Vendor Name", dataIndex: "vendorName" },
                    { title: "PO#", dataIndex: "poNo" },
                    { title: "Date", dataIndex: "poDate" },
                    { title: "Qty", render: (col, record) => <Number value={record.quantity} /> },
                    { title: "Rate", render: (col, record) => <Currency value={record.rate} /> }
                ]}
                    data={poList}
                    pagination={{ total: poListTotal, current: poPage }}
                    onChange={(pagination) => { setPOPage(pagination.current); getPOs(pagination.current); }}
                />
            </Drawer>
        </>
    )
}